import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://scrawl.codes';
const SITE_NAME = 'Scrawl';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;

// SEO utility functions for dynamic meta tag management
export const useSEO = () => {
  const location = useLocation();

  // Update page title and meta description
  const updateSEO = ({ title, description, keywords, canonical, ogImage, ogType = 'website', noIndex = false }) => {
    // Update title
    if (title) {
      document.title = `${title} | ${SITE_NAME}`;
      updateMetaTag('title', `${title} | ${SITE_NAME}`);
      updateMetaProperty('og:title', title);
      updateMetaTag('twitter:title', title);
    }

    // Update meta description
    if (description) {
      updateMetaTag('description', description);
      updateMetaProperty('og:description', description);
      updateMetaTag('twitter:description', description);
    }

    // Update keywords
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Update canonical URL
    if (canonical) {
      updateCanonical(canonical);
      updateMetaProperty('og:url', canonical);
      updateMetaTag('twitter:url', canonical);
    }

    // Update Open Graph image
    updateMetaProperty('og:image', ogImage || DEFAULT_IMAGE);
    updateMetaTag('twitter:image', ogImage || DEFAULT_IMAGE);
    
    // Update OG type
    updateMetaProperty('og:type', ogType);
    
    // Handle noindex for private pages
    if (noIndex) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      updateMetaTag('robots', 'index, follow, max-image-preview:large, max-snippet:-1');
    }
  };

  // Update meta tags
  const updateMetaTag = (name, content) => {
    let meta = document.querySelector(`meta[name="${name}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = name;
      document.head.appendChild(meta);
    }
    meta.content = content;
  };

  // Update meta properties (Open Graph, Twitter)
  const updateMetaProperty = (property, content) => {
    let meta = document.querySelector(`meta[property="${property}"]`);
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('property', property);
      document.head.appendChild(meta);
    }
    meta.content = content;
  };

  // Update canonical URL
  const updateCanonical = (url) => {
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = url;
  };

  // Set default SEO for different routes
  useEffect(() => {
    const path = location.pathname;

    switch (path) {
      case '/':
        updateSEO({
          title: 'Share Code and Text Snippets Instantly',
          description: 'Create and share code snippets with syntax highlighting for 100+ languages, password protection, and expiration options. The modern, free pastebin alternative for developers.',
          keywords: 'pastebin, code sharing, text sharing, syntax highlighting, code snippets, paste, programming tools, developer tools, github gist alternative, code editor online',
          canonical: `${BASE_URL}/`
        });
        break;

      case '/login':
        updateSEO({
          title: 'Login to Your Account',
          description: 'Sign in to Scrawl to access your saved pastes, create private snippets, and manage your code sharing history. Secure authentication with email verification.',
          keywords: 'scrawl login, sign in, account access, developer login',
          canonical: `${BASE_URL}/login`,
          noIndex: true
        });
        break;

      case '/register':
        updateSEO({
          title: 'Create Your Free Account',
          description: 'Join Scrawl for free to save your code snippets, create private pastes with password protection, and access advanced sharing features. Sign up takes seconds.',
          keywords: 'scrawl register, sign up, create account, free account, developer signup',
          canonical: `${BASE_URL}/register`,
          noIndex: true
        });
        break;

      case '/forgot-password':
        updateSEO({
          title: 'Reset Your Password',
          description: 'Forgot your Scrawl password? Reset it securely with email verification. Get back to sharing code in minutes.',
          keywords: 'password reset, forgot password, account recovery',
          canonical: `${BASE_URL}/forgot-password`,
          noIndex: true
        });
        break;

      case '/dashboard':
        updateSEO({
          title: 'Your Dashboard',
          description: 'Manage your code snippets, view paste statistics, and organize your shared content in your personal Scrawl dashboard.',
          keywords: 'dashboard, my pastes, code management, snippet manager',
          canonical: `${BASE_URL}/dashboard`,
          noIndex: true
        });
        break;

      case '/url-opener':
        updateSEO({
          title: 'URL Opener',
          description: 'Quick URL opener tool for navigating to pastes and resources.',
          keywords: 'url opener, quick navigation',
          canonical: `${BASE_URL}/url-opener`,
          noIndex: true
        });
        break;

      default:
        // Handle dynamic routes like /p/:shortId
        if (path.startsWith('/p/')) {
          // This will be handled by the individual paste component
          break;
        }
        break;
    }
  }, [location]);

  return { updateSEO };
};

// Generate structured data for paste pages
export const generatePasteStructuredData = (paste) => {
  const pasteUrl = `${BASE_URL}/p/${paste.shortId}`;
  
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareSourceCode',
    'name': paste.title || 'Code Snippet',
    'description': `A ${paste.language || 'code'} snippet shared on Scrawl - ${paste.content?.substring(0, 100) || 'Code sharing platform'}`,
    'programmingLanguage': {
      '@type': 'ComputerLanguage',
      'name': paste.language || 'Plain Text'
    },
    'codeRepository': pasteUrl,
    'author': paste.authorUsername ? {
      '@type': 'Person',
      'name': paste.authorUsername
    } : {
      '@type': 'Organization',
      'name': 'Anonymous'
    },
    'dateCreated': paste.createdAt,
    'datePublished': paste.createdAt,
    'text': paste.content?.substring(0, 500) + (paste.content?.length > 500 ? '...' : ''),
    'url': pasteUrl,
    'isAccessibleForFree': true,
    'provider': {
      '@type': 'Organization',
      'name': 'Scrawl',
      'url': BASE_URL
    }
  };

  // Remove undefined properties
  Object.keys(structuredData).forEach(key => 
    structuredData[key] === undefined && delete structuredData[key]
  );

  return structuredData;
};

// Generate breadcrumb structured data for paste pages
export const generateBreadcrumbData = (paste) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': BASE_URL
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': paste.title || 'Code Snippet',
        'item': `${BASE_URL}/p/${paste.shortId}`
      }
    ]
  };
};

// Inject structured data into page
export const injectStructuredData = (data, id = 'paste-structured-data') => {
  // Remove existing structured data with same ID
  const existingScript = document.querySelector(`script[data-id="${id}"]`);
  if (existingScript) {
    existingScript.remove();
  }

  // Create new script tag
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-id', id);
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};

// Generate social sharing meta tags for paste content
export const updatePasteSEO = (paste) => {
  const pasteUrl = `${BASE_URL}/p/${paste.shortId}`;
  const title = paste.title || 'Code Snippet';
  const langDisplay = paste.language ? paste.language.charAt(0).toUpperCase() + paste.language.slice(1) : 'Code';
  const description = `${langDisplay} snippet${paste.authorUsername ? ` by @${paste.authorUsername}` : ''} - ${paste.content?.substring(0, 120)?.replace(/\n/g, ' ') || 'Shared on Scrawl'}...`;

  // Update page title
  document.title = `${title} | ${SITE_NAME}`;

  // Update meta tags
  const updateMeta = (selector, content) => {
    const meta = document.querySelector(selector);
    if (meta) meta.content = content;
  };

  // Basic meta tags
  updateMeta('meta[name="description"]', description);
  updateMeta('meta[name="keywords"]', `${paste.language}, code snippet, ${paste.title || 'code'}, programming, developer`);
  
  // Open Graph
  updateMeta('meta[property="og:title"]', title);
  updateMeta('meta[property="og:description"]', description);
  updateMeta('meta[property="og:url"]', pasteUrl);
  updateMeta('meta[property="og:type"]', 'article');
  
  // Twitter
  updateMeta('meta[name="twitter:title"]', title);
  updateMeta('meta[name="twitter:description"]', description);
  updateMeta('meta[name="twitter:url"]', pasteUrl);

  // Update canonical URL
  const canonical = document.querySelector('link[rel="canonical"]');
  if (canonical) canonical.href = pasteUrl;

  // Generate and inject structured data
  const structuredData = generatePasteStructuredData(paste);
  injectStructuredData(structuredData, 'paste-structured-data');
  
  // Inject breadcrumb data
  const breadcrumbData = generateBreadcrumbData(paste);
  injectStructuredData(breadcrumbData, 'breadcrumb-structured-data');
};

export default useSEO;